import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Lightbulb, RefreshCw, Loader2, FileText, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAiQuota } from '@/lib/useAiQuota';
import { AiQuotaInline } from '@/components/AiQuotaInline';

// Sources displayed as filter options in the UI.
// reddit / youtube / tiktok are hidden until their integrations are implemented.
const SOURCES = ['google_search','google_maps','paa','ai_overview','trends','facebook'];
const SOURCE_LABELS = { google_search:'Google Search', google_maps:'Google Maps', paa:'People Also Ask', ai_overview:'AI Overview', trends:'Google Trends', reddit:'Reddit', youtube:'YouTube', facebook:'Facebook', tiktok:'TikTok' };
const PRIORITY_COLORS = { high:'bg-red-500/10 text-red-600', medium:'bg-amber-500/10 text-amber-600', low:'bg-green-500/10 text-green-600' };
const INTENT_LABELS = { transactional:'Transactionnel', informational:'Informatif', navigational:'Navigationnel' };
const INTENT_LABELS_EN = { transactional:'Transactional', informational:'Informational', navigational:'Navigational' };

export function OpportunitiesTab({ onGenerate }) {
  const { user } = useAuth();
  const { language: lang } = useLanguage();
  const isAdmin = ['admin', 'admin_enterprise'].includes(user?.role);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterSource, setFilterSource] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterIntent, setFilterIntent] = useState('all');

  const t = (fr, en) => lang === 'fr' ? fr : en;

  const { quota, refresh: refreshQuota, isReached } = useAiQuota();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const params = {};
      if (filterSource !== 'all') params.source = filterSource;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterIntent !== 'all') params.intent = filterIntent;
      const { data } = await api.get('/seo/opportunities', { params });
      setOpportunities(data.opportunities);
    } catch { toast.error(t('Erreur','Error')); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (!loading) { setLoading(true); load(); } }, [filterSource, filterPriority, filterIntent]);

  const generate = async () => {
    setGenerating(true);
    // Snapshot old data so we can compare after the request returns; the
    // backend may complete the generation even if the HTTP call itself
    // errors (proxy/network timeout while Claude is still writing to Mongo).
    const previousCount = opportunities.length;
    const previousFirstId = opportunities[0]?.opportunity_id || null;

    // Helper to rebuild the filter params used by the GET endpoint so the
    // post-generation reload honors the user's active filters.
    const buildParams = () => {
      const params = {};
      if (filterSource !== 'all') params.source = filterSource;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterIntent !== 'all') params.intent = filterIntent;
      return params;
    };

    try {
      // Claude generation realistically takes 30-60s. Override the default
      // axios timeout for THIS call only (no api.js change needed).
      await api.post('/seo/opportunities/generate', null, { timeout: 120000 });
      // Re-GET so active filters are reapplied — the POST response returns
      // the FULL freshly generated list (unfiltered) which would otherwise
      // wipe the user's selected filter view.
      const { data: fresh } = await api.get('/seo/opportunities', { params: buildParams() });
      setOpportunities(fresh.opportunities);
      toast.success(t(
        `${fresh.opportunities.length} opportunités générées`,
        `${fresh.opportunities.length} opportunities generated`
      ));
      refreshQuota();
    } catch (e) {
      // Friendly quota-reached path BEFORE the timeout recovery logic.
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'ai_quota_reached') {
        toast.error(t(
          'Limite IA atteinte pour ce mois. Passez au forfait supérieur ou contactez l\'administrateur.',
          'AI limit reached for this month. Upgrade your plan or contact your administrator.'
        ));
        refreshQuota();
        return;
      }
      // Recovery path: POST failed/timed-out from the client side, but the
      // backend may still have persisted the new opportunities. Do a GET
      // and compare; if the dataset clearly changed, treat as success.
      try {
        const { data: fresh } = await api.get('/seo/opportunities', { params: buildParams() });
        const freshFirstId = fresh.opportunities[0]?.opportunity_id || null;
        const datasetChanged = (
          freshFirstId !== previousFirstId ||
          fresh.opportunities.length !== previousCount
        );
        if (datasetChanged) {
          setOpportunities(fresh.opportunities);
          toast.success(t(
            `${fresh.opportunities.length} opportunités générées`,
            `${fresh.opportunities.length} opportunities generated`
          ));
          return;
        }
      } catch {
        // Fall through to the real-error toast below.
      }
      // Real failure: keep the previously displayed opportunities untouched
      // (we never called setOpportunities([])) and show an explicit message.
      const isTimeout = e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message || ''));
      const detailMsg = typeof e?.response?.data?.detail === 'string'
        ? e.response.data.detail
        : null;
      toast.error(
        isTimeout
          ? t(
              'La génération a pris trop de temps. Les anciennes données sont conservées.',
              'Generation timed out. Previous data is preserved.'
            )
          : (detailMsg || t('Erreur de génération', 'Generation error'))
      );
    } finally {
      setGenerating(false);
    }
  };

  if (loading && opportunities.length === 0) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="seo-opportunities-tab">
      <div className="flex flex-wrap items-center gap-3">
        {isAdmin && (
          <div className="flex flex-col items-start gap-1.5">
            <Button onClick={generate} disabled={generating || isReached} data-testid="generate-opportunities-btn">
              {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {generating ? t('Generation...','Generating...') : (isReached ? t('Quota atteint', 'Quota reached') : t('Rafraichir','Refresh'))}
            </Button>
            <AiQuotaInline quota={quota} />
          </div>
        )}
        <Select value={filterSource} onValueChange={setFilterSource} disabled={generating}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('Toutes sources','All sources')}</SelectItem>
            {SOURCES.map(s => <SelectItem key={s} value={s}>{SOURCE_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority} disabled={generating}>
          <SelectTrigger className="w-36"><SelectValue placeholder={t('Priorite','Priority')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('Toutes','All')}</SelectItem>
            <SelectItem value="high">{t('Haute','High')}</SelectItem>
            <SelectItem value="medium">{t('Moyenne','Medium')}</SelectItem>
            <SelectItem value="low">{t('Basse','Low')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterIntent} onValueChange={setFilterIntent} disabled={generating}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Intent" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('Toutes','All')}</SelectItem>
            <SelectItem value="transactional">{t('Transactionnel','Transactional')}</SelectItem>
            <SelectItem value="informational">{t('Informatif','Informational')}</SelectItem>
            <SelectItem value="navigational">{t('Navigationnel','Navigational')}</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground ml-auto">{opportunities.length} {t('opportunites','opportunities')}</span>
      </div>

      {generating && (
        <Card className="border-primary/30 bg-primary/5" data-testid="opp-generating-banner">
          <CardContent className="py-3 flex items-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
            <p className="text-sm">
              {t(
                'Génération IA en cours — cela peut prendre 30 à 60 secondes. Les opportunités actuelles restent visibles ci-dessous.',
                'AI generation in progress — this can take 30 to 60 seconds. Current opportunities stay visible below.'
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {opportunities.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-40" />
          <p className="font-medium">{t('Aucune opportunite','No opportunities')}</p>
          <p className="text-sm">{t('Lancez une analyse pour generer des opportunites SEO','Run an analysis to generate SEO opportunities')}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {opportunities.map(o => (
            <Card key={o.opportunity_id} className="hover:shadow-sm transition-shadow">
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{o.title}</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">{o.reason}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Badge variant="outline" className="text-xs">{o.keyword}</Badge>
                      <Badge className={`text-xs ${PRIORITY_COLORS[o.priority]}`}>{o.priority}</Badge>
                      <Badge variant="secondary" className="text-xs">{SOURCE_LABELS[o.source] || o.source}</Badge>
                      <Badge variant="outline" className="text-xs">{lang === 'fr' ? (INTENT_LABELS[o.intent] || o.intent) : (INTENT_LABELS_EN[o.intent] || o.intent)}</Badge>
                    </div>
                  </div>
                  <div className="text-right shrink-0 space-y-1">
                    <div className="text-sm"><span className="text-muted-foreground">Vol: </span><span className="font-medium">{o.volume_estimate}</span></div>
                    <div className="text-sm"><span className="text-muted-foreground">{t('Diff','Diff')}: </span><span className={`font-medium ${o.difficulty > 70 ? 'text-red-500' : o.difficulty > 40 ? 'text-amber-500' : 'text-green-500'}`}>{o.difficulty}/100</span></div>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onGenerate && onGenerate(o)} data-testid={`generate-article-${o.opportunity_id}`}>
                        <FileText className="mr-1 h-3 w-3" />{t('Article','Article')} <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
